// ─── ERP AI Agent Frontend Service ───────────────────────────────────────────
// Optimized for minimum token consumption:
// - Client-side 5-min cache for repeated queries
// - Handles sql_bypass responses (no AI call needed)
// - Robust error parsing with real server messages

import { supabase } from '../lib/supabase';

// ─── CLIENT-SIDE CACHE (5 minutes) ───────────────────────────────────────────
const clientCache = new Map();
const CLIENT_CACHE_TTL = 5 * 60 * 1000;

function getCacheKey(userId, message) {
  return `${userId}::${message?.trim()?.toLowerCase()?.replace(/\s+/g, ' ')}`;
}

function getClientCache(key) {
  const entry = clientCache?.get(key);
  if (!entry) return null;
  if (Date.now() > entry?.expiresAt) {
    clientCache?.delete(key);
    return null;
  }
  return entry?.data;
}

function setClientCache(key, data) {
  if (clientCache?.size > 100) {
    const now = Date.now();
    for (const [k, v] of clientCache?.entries()) {
      if (now > v?.expiresAt) clientCache?.delete(k);
    }
  }
  clientCache?.set(key, { data, expiresAt: Date.now() + CLIENT_CACHE_TTL });
}

// ─── SEND MESSAGE ─────────────────────────────────────────────────────────────

/**
 * Send a message to the ERP AI Agent.
 * @param {string} message - User's query
 * @param {string|null} conversationId - Existing conversation UUID (optional)
 * @param {object} context - Current page context
 * @returns {Promise<AgentResponse>}
 */
export async function sendAgentMessage(message, conversationId = null, context = {}) {
  // 1. Validate session
  const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
  if (sessionError || !sessionData?.session) {
    throw new Error('Sesión no encontrada. Por favor, cierra sesión y vuelve a iniciar sesión.');
  }
  const session = sessionData?.session;
  if (!session?.access_token) {
    throw new Error('Token de acceso no disponible. Por favor, vuelve a iniciar sesión.');
  }

  // 2. Check client-side cache before calling Edge Function
  const userId = session?.user?.id || 'unknown';
  const cacheKey = getCacheKey(userId, message);
  const cached = getClientCache(cacheKey);
  if (cached) {
    console.log('[erpAiAgentService] Cache HIT (cliente)');
    return { ...cached, from_cache: true };
  }

  // 3. Invoke Edge Function
  const { data, error } = await supabase?.functions?.invoke('erp-ai-agent', {
    body: {
      message,
      conversation_id: conversationId,
      context: {
        module: 'IA_EMPRESARIAL',
        current_page: context?.current_page || '/ia-empresarial',
        ...context,
      },
    },
  });

  // 4. Handle invocation errors — read real body via error.context
  if (error) {
    console.error('[erpAiAgentService] Error Edge Function:', error);

    let errorDetail = error?.message || 'Error desconocido';
    let responseBody = null;

    try {
      if (error?.context) {
        responseBody = await error?.context?.json();
        console.error('[erpAiAgentService] Respuesta real de la Edge Function:', responseBody);

        if (responseBody?.code === 429 || responseBody?.retryable === true) {
          const quotaErr = new Error('Se ha alcanzado temporalmente el límite de uso de Gemini. Espera unos minutos y vuelve a intentarlo.');
          quotaErr.code = 429;
          quotaErr.retryable = true;
          throw quotaErr;
        }

        errorDetail = responseBody?.error || responseBody?.message || responseBody?.details || errorDetail;
      }
    } catch (parseError) {
      if (parseError?.code === 429) throw parseError;
      console.error('[erpAiAgentService] No se pudo leer el cuerpo del error:', parseError);
    }

    if (errorDetail?.includes('Usuario no encontrado') || errorDetail?.includes('user not found')) {
      throw new Error('Usuario no encontrado en el sistema. Contacta con el administrador.');
    }
    if (errorDetail?.includes('Perfil no encontrado') || errorDetail?.includes('profile not found')) {
      throw new Error('Perfil de usuario no encontrado. Contacta con el administrador.');
    }
    if (errorDetail?.includes('inactivo') || errorDetail?.includes('inactive')) {
      throw new Error('Tu cuenta está inactiva. Contacta con el administrador.');
    }
    if (errorDetail?.includes('No autenticado') || errorDetail?.includes('Unauthorized') || errorDetail?.includes('JWT')) {
      throw new Error('Sesión expirada. Por favor, vuelve a iniciar sesión.');
    }
    if (errorDetail?.includes('GEMINI_API_KEY')) {
      throw new Error('Falta la variable GEMINI_API_KEY. Contacta con el administrador.');
    }

    throw new Error(errorDetail);
  }

  // 5. Handle application-level errors in response body
  if (!data?.success) {
    const appErr = data?.error || data?.error_detail || '';
    console.error('[erpAiAgentService] Error en respuesta:', data);

    if (data?.code === 429 || data?.retryable === true) {
      const quotaErr = new Error('Se ha alcanzado temporalmente el límite de uso de Gemini. Espera unos minutos y vuelve a intentarlo.');
      quotaErr.code = 429;
      quotaErr.retryable = true;
      quotaErr.conversationId = data?.conversation_id;
      quotaErr.answer = data?.answer;
      throw quotaErr;
    }

    if (appErr?.includes('Usuario no encontrado')) throw new Error('Usuario no encontrado en el sistema. Contacta con el administrador.');
    if (appErr?.includes('Perfil no encontrado')) throw new Error('Perfil de usuario no encontrado. Contacta con el administrador.');
    if (appErr?.includes('inactivo') || appErr?.includes('is_active')) throw new Error('Tu cuenta está inactiva. Contacta con el administrador.');
    if (appErr?.includes('No autenticado') || appErr?.includes('token') || appErr?.includes('JWT')) throw new Error('Sesión no válida. Por favor, vuelve a iniciar sesión.');
    if (appErr?.includes('GEMINI_API_KEY')) throw new Error('Falta la variable GEMINI_API_KEY. Contacta con el administrador.');

    throw new Error(appErr || 'Error al procesar la solicitud del agente IA.');
  }

  // 6. Cache successful responses (server cache hit or new result)
  if (data?.success && !data?.gemini_error) {
    setClientCache(cacheKey, data);
  }

  return data;
}

/**
 * Load conversation list for the current user.
 */
export async function loadConversations(limit = 20) {
  const { data, error } = await supabase?.schema('tienda')?.from('ai_conversations')
    ?.select('id, titulo, estado, created_at, updated_at')
    ?.eq('estado', 'ACTIVA')
    ?.order('updated_at', { ascending: false })
    ?.limit(limit);

  if (error) throw new Error(`Error cargando conversaciones: ${error.message}`);
  return data || [];
}

/**
 * Load messages for a specific conversation.
 */
export async function loadConversationMessages(conversationId) {
  const { data, error } = await supabase?.schema('tienda')?.from('ai_messages')
    ?.select('id, role, content, metadata, created_at')
    ?.eq('conversation_id', conversationId)
    ?.in('role', ['user', 'assistant'])
    ?.order('created_at', { ascending: true });

  if (error) throw new Error(`Error cargando mensajes: ${error.message}`);
  return data || [];
}

/**
 * Archive a conversation (soft delete).
 */
export async function archiveConversation(conversationId) {
  const { error } = await supabase?.schema('tienda')?.from('ai_conversations')
    ?.update({ estado: 'ARCHIVADA', updated_at: new Date()?.toISOString() })
    ?.eq('id', conversationId);

  if (error) throw new Error(`Error archivando conversación: ${error.message}`);
}

/**
 * Clear the client-side cache (e.g., after data updates).
 */
export function clearAgentCache() {
  clientCache?.clear();
}
